<template>
  <v-form
    class="pl-2 border-bottom"
    ref="measurementForm"
    v-model="isMeasurementValid"
    no-gutters
    style="display: table-row"
  >
    <template v-if="isNewMeasurement">
      <div v-for="input in inputsForm" :key="input.name" class="pt-2 customtable-tablecell">
        <data-input v-model="measurementModifyData[input.name]" v-bind="input" clearable class="px-2" />
      </div>
      <div v-if="!isSaving" class="options justify-center customtable-tablecell">
        <div>
          <v-tooltip color="font" bottom>
            <template v-slot:activator="{ on }">
              <v-icon
                v-on="on"
                :style="`cursor: ${isMeasurementValid ? 'pointer' : 'default'}`"
                :color="isMeasurementValid ? 'success' : 'grey'"
                @click="addMeasurementHandler"
              >
                mdi-check
              </v-icon>
            </template>
            <span>
              {{ isMeasurementValid ? $i18n.t('dialog.body.addMeasurement') : $i18n.t('dialog.body.toAddMeasurement') }}
            </span>
          </v-tooltip>
        </div>
      </div>
      <div v-else class="options customtable-tablecell">
        <main-loader size="24" width="2" />
      </div>
    </template>
    <template v-else>
      <div
        v-for="input in inputsEdit"
        :key="input.name"
        class="customtable-tablecell"
        :class="isCurrentlyEditing ? 'pt-2' : 'py-2'"
      >
        <data-input
          v-if="isCurrentlyEditing"
          v-model="measurementModifyData[input.name]"
          v-bind="input"
          class="px-2"
          clearable
        />
        <span v-else>
          {{
            measurement[input.name] && measurement[input.name]['verbose_name']
              ? measurement[input.name]['verbose_name']
              : measurement[input.name]
          }}
        </span>
      </div>
      <div v-if="!isCurrentlyEditing && !isSaving" class="py-2 customtable-tablecell options">
        <div class="d-flex justify-center">
          <v-tooltip color="font" bottom>
            <template v-slot:activator="{ on }">
              <v-icon v-on="on" @click="getMeasurementToEdit(measurement)" class="measurement-icons">
                mdi-pencil
              </v-icon>
            </template>
            <span> {{ $i18n.t('dialog.body.editMeasurement') }} </span>
          </v-tooltip>
          <v-tooltip color="font" bottom>
            <template v-slot:activator="{ on }">
              <v-icon v-on="on" @click="() => $emit('openDeletingDialog', measurement.id)" class="measurement-icons">
                mdi-delete
              </v-icon>
            </template>
            <span> {{ $i18n.t('dialog.body.deleteMeasurement') }} </span>
          </v-tooltip>
        </div>
      </div>
      <div v-if="isCurrentlyEditing && !isSaving" class="options customtable-tablecell">
        <v-tooltip color="font" bottom>
          <template v-slot:activator="{ on }">
            <v-icon
              v-on="on"
              :style="`cursor: ${isMeasurementValid ? 'pointer' : 'default'}`"
              class="measurement-icons"
              :color="isMeasurementValid ? 'success' : 'grey'"
              @click="editMeasurementHandler"
            >
              mdi-check
            </v-icon>
          </template>
          <span>
            {{
              isMeasurementValid
                ? $i18n.t('dialog.body.saveChangesInMeasurement')
                : $i18n.t('dialog.body.toEditMeasurement')
            }}
          </span>
        </v-tooltip>
        <v-tooltip color="font" bottom>
          <template v-slot:activator="{ on }">
            <v-icon v-on="on" class="measurement-icons" color="error" @click="clearChanges()">
              mdi-close
            </v-icon>
          </template>
          <span> {{ $i18n.t('dialog.body.deleteChangesInMeasurement') }} </span>
        </v-tooltip>
      </div>
      <div v-else-if="isSaving" class="options customtable-tablecell">
        <main-loader size="24" width="2" />
      </div>
    </template>
  </v-form>
</template>

<script>
import { get, call } from 'vuex-pathify';

import DataInput from '@/components/DataInput';
import MainLoader from '@/components/MainLoader';

export default {
  name: 'ScadaManagerTableRow',
  components: {
    DataInput,
    MainLoader,
  },
  props: {
    isNewMeasurement: {
      type: Boolean,
      default: false,
    },
    measurement: {
      type: Object,
      default() {
        return {};
      },
    },
    scadaSystems: {
      type: Object,
      default() {
        return {};
      },
    },
    measurements: {
      type: Array,
      default() {
        return [];
      },
    },
    measurementParameters: {
      type: Array,
      default() {
        return [];
      },
    },
    currentlyEditedMeasurementId: {
      type: Number,
      default: null,
    },
  },
  data: () => ({
    measurementModifyData: {
      external_name: null,
      verbose_name: null,
      system: null,
      measure_type: null,
      report_period: null,
      measurement_parameter: null,
      measurement_unit: null,
    },
    isMeasurementValid: false,
    isSaving: false,
  }),
  computed: {
    scadaDataSourceName: get('admin/modulesMapping@scada3.measurements_datasource_name'),
    currentlyEditedMeasurementIdProxy: {
      get() {
        return this.currentlyEditedMeasurementId;
      },
      set(nV) {
        this.$emit('update:currentlyEditedMeasurementId', nV);
      },
    },
    isCurrentlyEditing() {
      return this.measurement.id === this.currentlyEditedMeasurementIdProxy;
    },
    isVerboseNameTaken() {
      if (this.isCurrentlyEditing) {
        return this.measurements
          .filter(measurement => measurement.id !== this.currentlyEditedMeasurementIdProxy)
          .map(measurement => measurement.verbose_name);
      } else {
        return this.measurements.map(measurement => measurement.verbose_name);
      }
    },
    inputsForm() {
      return [
        {
          name: 'external_name',
          translatePath: 'dialog.body.measurementSeriesIdentifier',
          dataType: { name: 'text' },
          rules: [v => !!v || v == '0' || 'rules.required'],
        },
        {
          name: 'verbose_name',
          translatePath: 'dialog.body.verboseName',
          dataType: { name: 'text' },
          rules: [
            v => !!v || v == '0' || 'rules.required',
            v => !this.isVerboseNameTaken.includes(v) || 'rules.verboseNameIsTaken',
          ],
        },
        {
          name: 'system',
          translatePath: 'dialog.body.scadaType',
          dataType: { name: 'select' },
          items: Object.keys(this.scadaSystems),
          rules: [v => !!v || v == '0' || 'rules.required'],
        },
        {
          name: 'measure_type',
          translatePath: 'dialog.body.measureType',
          dataType: { name: 'select' },
          items: this.getScadaSystemsItems('supported_measures'),
          itemValue: 'name',
          itemText: 'verbose_name',
          rules: [v => !!v || v == '0' || 'rules.required'],
          disabled: !this.measurementModifyData.system,
        },
        {
          name: 'report_period',
          translatePath: 'dialog.body.timeInterval',
          dataType: { name: 'select' },
          items: this.getScadaSystemsItems('aggregate_periods'),
          itemValue: 'name',
          itemText: 'verbose_name',
          disabled: !this.measurementModifyData.system || this.measurementModifyData.measure_type !== 'aggregate',
        },
        {
          name: 'measurement_parameter',
          translatePath: 'dialog.body.parameter',
          dataType: { name: 'select' },
          items: this.measurementParameters,
          itemValue: 'name',
          itemText: 'verbose_name',
          optional: true,
        },
        {
          name: 'measurement_unit',
          translatePath: 'dialog.body.measurementUnit',
          dataType: { name: 'text' },
          optional: true,
        },
      ];
    },
    inputsEdit() {
      return [
        {
          name: 'external_name',
          dataType: { name: 'text' },
          rules: [v => !!v || v == '0' || 'rules.required'],
        },
        {
          name: 'verbose_name',
          dataType: { name: 'text' },
          rules: [
            v => !!v || v == '0' || 'rules.required',
            v => !this.isVerboseNameTaken.includes(v) || 'rules.verboseNameIsTaken',
          ],
        },
        {
          name: 'system',
          dataType: { name: 'select' },
          items: Object.keys(this.scadaSystems),
          rules: [v => !!v || v == '0' || 'rules.required'],
        },
        {
          name: 'measure_type',
          dataType: { name: 'select' },
          items: this.getScadaSystemsItems('supported_measures'),
          itemValue: 'name',
          itemText: 'verbose_name',
          rules: [v => !!v || v == '0' || 'rules.required'],
          disabled: !this.measurementModifyData.system,
        },
        {
          name: 'report_period',
          dataType: { name: 'select' },
          items: this.getScadaSystemsItems('aggregate_periods'),
          itemValue: 'name',
          itemText: 'verbose_name',
          disabled: !this.measurementModifyData.system || this.measurementModifyData.measure_type !== 'aggregate',
        },
        {
          name: 'measurement_parameter',
          dataType: { name: 'select' },
          items: this.measurementParameters,
          itemValue: 'name',
          itemText: 'verbose_name',
        },
        {
          name: 'measurement_unit',
          dataType: { name: 'text' },
        },
      ];
    },
  },
  methods: {
    addMeasurement: call('layers/addDatasourceFeature'),
    getScadaMeasurements: call('scada/getScadaThreeMeasurements'),
    editionMeasurement: call('layers/editDatasourceFeature'),
    async addMeasurementHandler() {
      const data = this.measurementModifyData;
      if (!this.isMeasurementValid) {
        return;
      }
      if (data.measure_type !== 'aggregate') {
        data.report_period = null;
      }
      const payload = {
        datasource: this.scadaDataSourceName,
        body: data,
      };
      try {
        this.isSaving = true;
        await this.addMeasurement(payload);
        await this.getScadaMeasurements(this.scadaDataSourceName);
        this.$emit('getMeasurements', false);
        this.clearForm();
      } catch {
        //
      } finally {
        this.isSaving = false;
      }
    },
    async editMeasurementHandler() {
      const data = this.measurementModifyData;
      if (data && !this.isMeasurementValid) {
        return;
      }
      if (data.measure_type !== 'aggregate') {
        data.report_period = null;
      }
      const payload = {
        datasource: this.scadaDataSourceName,
        body: data,
        id: this.currentlyEditedMeasurementIdProxy,
      };
      try {
        this.isSaving = true;
        await this.editionMeasurement(payload);
        await this.getScadaMeasurements(this.scadaDataSourceName);
        this.$emit('getMeasurements', false);
        this.currentlyEditedMeasurementIdProxy = null;
        this.clearChanges();
      } catch {
        //
      } finally {
        this.isSaving = false;
      }
    },
    clearForm() {
      this.$refs?.measurementForm?.resetValidation();
      this.measurementModifyData = {
        external_name: null,
        verbose_name: null,
        system: null,
        measure_type: null,
        report_period: null,
        measurement_parameter: null,
        measurement_unit: null,
      };
    },
    clearChanges() {
      this.$refs?.measurementForm?.resetValidation();
      this.currentlyEditedMeasurementIdProxy = null;
      this.measurementModifyData = {
        external_name: null,
        verbose_name: null,
        system: null,
        measure_type: null,
        report_period: null,
        measurement_parameter: null,
        measurement_unit: null,
      };
    },
    getScadaSystemsItems(items) {
      if (this.isNewMeasurement && this.measurementModifyData.system) {
        return this.scadaSystems[this.measurementModifyData.system][items]
          ? Object.values(this.scadaSystems[this.measurementModifyData.system][items])
          : [];
      } else if (this.measurementModifyData.system) {
        return this.scadaSystems[this.measurementModifyData.system][items]
          ? Object.values(this.scadaSystems[this.measurementModifyData.system][items])
          : [];
      } else {
        return [];
      }
    },
    getMeasurementToEdit(measurement) {
      this.currentlyEditedMeasurementIdProxy = measurement.id;
      this.measurementModifyData = {
        external_name: measurement.external_name,
        verbose_name: measurement.verbose_name,
        system: measurement.system,
        measure_type: measurement.measure_type.name,
        report_period: measurement.report_period.name,
        measurement_parameter: measurement.measurement_parameter.name,
        measurement_unit: measurement.measurement_unit,
      };
    },
  },
};
</script>

<style lang="scss" scoped>
.border-bottom {
  @include border(bottom, 1px);
}

.measurement-icons:hover {
  color: black;
}

.options {
  position: sticky;
  right: 0;
  z-index: 1;
  min-width: 80px;
  background-color: rgba(#fff, 1);
  &::before {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    @include border(left, 1px);
  }
}
</style>
